import { RubyGemMark } from '../../../components/RubyGemMark';
import React, { useRef, useState } from 'react';
import { Upload, Trash2, AlertTriangle, ImageOff } from 'lucide-react';
import ShimmerImage from '../../../components/ShimmerImage';
import { hardwareApi } from '../../../services/hardwareApi';
import { toastService } from '../../../services/toastService';

/**
 * Photo gallery backed by hardware-service's presigned S3 PUT.
 *
 * NOT uploads-service: that is gated by the TENANT Cognito pool, and super-admin
 * authenticates against the platform ADMIN pool, so its presign returns 401 from
 * this app. hardware-service uses the admin pool, same as device-config-service.
 */

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
/** Kept in step with hardware-service's own `VIDEO_TYPES` / `LIMITS.video`. */
const VIDEO_MAX_BYTES = 120 * 1024 * 1024;
const VIDEO_ACCEPTED = ['video/mp4', 'video/webm', 'video/quicktime'];

interface Props {
  photos: string[];
  heroImageUrl?: string;
  /**
   * SHORT DEMO VIDEO, in the same gallery. Both kinds live in one S3 bucket and
   * one grid, because to an operator they are both "pictures of the thing".
   *
   * A video is never the hero — deal cards, order rows and bundle cards render
   * a still, so a video there would render as nothing. The hero stays a photo.
   */
  videos?: string[];
  onChange: (next: { photos: string[]; heroImageUrl?: string; videos?: string[] }) => void;
  /** Shown in the empty state so it reads as "photos of THIS device". */
  subject?: string;
  /** Namespaces the S3 key so an orphaned photo is traceable to its device. */
  modelId?: string;
}

const ImageGallery: React.FC<Props> = ({ photos, heroImageUrl, videos = [], onChange, subject, modelId }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // The hero is whichever photo is marked, else the first. Keeping it derived
  // means the two can never disagree about which image is the hero.
  const hero = heroImageUrl && photos.includes(heroImageUrl) ? heroImageUrl : photos[0];

  /** Which button opened the dialog — it narrows the file filter. */
  const [pickKind, setPickKind] = useState<'photo' | 'video'>('photo');
  const pick = (kind: 'photo' | 'video' = 'photo') => {
    setPickKind(kind);
    // The `accept` attribute is read when the dialog opens, so it is set first.
    requestAnimationFrame(() => fileRef.current?.click());
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setError(null);
    setBusy(true);
    const added: string[] = [];
    const addedVideos: string[] = [];
    try {
      for (const file of Array.from(files)) {
        const isVideo = VIDEO_ACCEPTED.includes(file.type);
        if (!ACCEPTED.includes(file.type) && !isVideo) {
          setError(`${file.name}: photos (PNG, JPEG, WebP, GIF) or video (MP4, WebM, MOV)`);
          continue;
        }
        // The service applies the real cap per kind; this is the same number
        // stated early, so a 90 MB video is refused before it is uploaded.
        const cap = isVideo ? VIDEO_MAX_BYTES : MAX_BYTES;
        if (file.size > cap) {
          setError(`${file.name}: over ${Math.round(cap / (1024 * 1024))} MB`);
          continue;
        }
        const { uploadUrl, fileUrl } = await hardwareApi.presignPhoto({
          modelId, filename: file.name, contentType: file.type, bytes: file.size,
        });
        const put = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        });
        if (!put.ok) throw new Error(`upload failed (${put.status})`);
        (isVideo ? addedVideos : added).push(fileUrl);
      }
      if (added.length || addedVideos.length) {
        const nextPhotos = [...photos, ...added];
        const nextVideos = [...videos, ...addedVideos];
        onChange({
          photos: nextPhotos,
          // The hero is only ever a photo, so uploading a video never changes it.
          heroImageUrl: hero || nextPhotos[0],
          videos: nextVideos,
        });
        const n = added.length + addedVideos.length;
        toastService.success(`${n} file${n === 1 ? '' : 's'} uploaded`);
      }
    } catch (e: any) {
      setError(e?.message || 'Upload failed');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  /** A video has no hero to reassign, so removal is simpler than a photo's. */
  const removeVideo = async (url: string) => {
    onChange({ photos, heroImageUrl, videos: videos.filter((v) => v !== url) });
    try {
      await hardwareApi.deletePhoto(url);
    } catch {
      toastService.warning('Removed from this device, but the file could not be deleted from storage.');
    }
  };

  const remove = async (url: string) => {
    setConfirmDelete(null);
    const next = photos.filter((p) => p !== url);
    // Drop the reference immediately — being unable to remove a photo from the
    // device is a real block, whereas a leftover S3 object is a minor cost.
    onChange({
      photos: next,
      heroImageUrl: url === hero ? next[0] : heroImageUrl,
      videos,
    });
    try {
      await hardwareApi.deletePhoto(url);
    } catch (e: any) {
      // Not silent: say plainly that the file is still in storage.
      toastService.warning('Removed from this device, but the file could not be deleted from storage.');
    }
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="dq-sechead">Media</h3>
        {/* TWO BUTTONS, not one. A single "Upload" never told anybody video was
            allowed at all — the capability existed and was invisible. */}
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => pick('photo')}
            disabled={busy}
            className="flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12px] font-semibold disabled:opacity-50"
            style={{ borderColor: 'rgba(220,20,60,.3)', color: '#DC143C', background: '#FFF1F3' }}
          >
            {busy ? <RubyGemMark size={13} drawing color="currentColor" /> : <Upload size={13} />}
            Upload photo
          </button>
          <button
            type="button"
            onClick={() => pick('video')}
            disabled={busy}
            className="flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12px] font-semibold disabled:opacity-50"
            style={{ borderColor: 'rgba(220,20,60,.3)', color: '#DC143C', background: '#FFF1F3' }}
          >
            <Upload size={13} /> Upload video
          </button>
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept={(pickKind === 'video' ? VIDEO_ACCEPTED : ACCEPTED).join(',')}
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      {error && (
        <div className="mb-2 flex items-start gap-1.5 rounded-lg border border-red-200 bg-red-50 p-2 text-[11px] text-red-800">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      {photos.length === 0 && videos.length === 0 ? (
        <button
          type="button"
          onClick={() => pick('photo')}
          className="flex w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 py-6 text-gray-500 hover:border-gray-400 hover:bg-gray-50"
        >
          <Upload size={18} className="mb-1" />
          <span className="text-xs font-medium">Add photos or video</span>
          <span className="mt-0.5 text-[10px] text-gray-400">
            Photo up to 5 MB · video up to 120 MB
          </span>
        </button>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-5">
            {photos.map((url) => {
              const isHero = url === hero;
              return (
                <div key={url} className="group relative">
                  <button
                    type="button"
                    onClick={() => onChange({ photos, heroImageUrl: url, videos })}
                    title={isHero ? 'Hero image' : 'Make this the hero image'}
                    className={`block w-full overflow-hidden rounded border-2 transition ${
                      isHero ? 'border-emerald-500' : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <ShimmerImage
                      src={url}
                      alt="device photo"
                      aspectRatio="1/1"
                      objectFit="cover"
                      fallbackBgColor="bg-gray-800"
                      fallbackIcon={<ImageOff size={14} className="text-gray-500" />}
                      variant="dark"
                    />
                  </button>

                  <span
                    className="pointer-events-none absolute left-1 top-1 rounded-full px-1.5 py-[1px] text-[8px] font-bold tracking-wider text-white"
                    style={{ background: isHero ? '#DC143C' : 'rgba(28,25,23,.72)' }}
                  >
                    {isHero ? 'HERO' : 'PHOTO'}
                  </span>

                  <button
                    type="button"
                    onClick={() => setConfirmDelete(url)}
                    title="Remove photo"
                    className="absolute right-1 top-1 rounded bg-black/60 p-1 text-white opacity-0 transition group-hover:opacity-100 hover:bg-red-600"
                  >
                    <Trash2 size={10} />
                  </button>
                </div>
              );
            })}

            {/* VIDEO TILES. Rendered from the real file so a poster frame is
                not needed — `preload="metadata"` fetches only the header, not
                the whole clip, so a 100 MB video costs a few KB to show here. */}
            {videos.map((url) => (
              <div key={url} className="group relative">
                <video
                  src={url}
                  preload="metadata"
                  className="aspect-square w-full rounded border border-gray-200 bg-black object-cover"
                  onClick={(e) => {
                    const v = e.currentTarget;
                    if (v.paused) v.play(); else v.pause();
                  }}
                />
                <span className="pointer-events-none absolute left-1 top-1 rounded bg-black/70 px-1.5 py-0.5 text-[8.5px] font-bold tracking-wider text-white">
                  VIDEO
                </span>
                <button
                  type="button"
                  onClick={() => removeVideo(url)}
                  title="Remove video"
                  className="absolute right-1 top-1 rounded bg-black/60 p-1 text-white opacity-0 transition group-hover:opacity-100 hover:bg-red-600"
                >
                  <Trash2 size={10} />
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={() => pick('photo')}
              disabled={busy}
              className="flex aspect-square items-center justify-center rounded border-2 border-dashed border-gray-300 text-gray-400 hover:border-gray-400 hover:bg-gray-50 disabled:opacity-50"
            >
              {busy ? <RubyGemMark size={16} drawing color="currentColor" /> : <Upload size={16} />}
            </button>
          </div>
          <p className="mt-2 text-[11px] text-gray-500">
            Click a photo to make it the hero — that is the one used on deal cards and the landing page.
          </p>
        </>
      )}

      {confirmDelete && (
        <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-xs font-semibold text-red-900">Remove this photo?</p>
          <p className="mt-0.5 text-[11px] text-red-800">It is deleted from storage and cannot be recovered.</p>
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirmDelete(null)}
              className="rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => remove(confirmDelete)}
              className="rounded-lg bg-red-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-red-700"
            >
              Remove
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageGallery;
